/**
 * 仪表盘 / 成本统计图表的纯数据整形函数 (无 React、无请求, 便于单测)。
 *
 * 日期口径与后端一致: 统计按业务时区 (STATS_TIMEZONE, 默认 Asia/Shanghai) 记日期,
 * 所以「今天」「本月」也必须按该时区算, 不能用浏览器本地时区或 toISOString (UTC)。
 */
import type { DailyStatRow, MonthlyStatRow, UsageTotals } from "../cost/api";

export const STATS_TZ = "Asia/Shanghai";

/** 某时刻在指定时区下的日期 "YYYY-MM-DD" */
export function ymdInTz(now: Date, tz: string = STATS_TZ): string {
  // en-CA 的日期格式正好是 YYYY-MM-DD
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(now);
}

/** "YYYY-MM-DD" 平移 delta 天 (按日历日算, 与时区无关) */
export function shiftDay(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + delta));
  return t.toISOString().slice(0, 10);
}

/** 以 end 结尾 (含) 的连续 n 天, 升序 */
export function lastNDays(end: string, n: number): string[] {
  return Array.from({ length: n }, (_, i) => shiftDay(end, i - (n - 1)));
}

/** 这些日期覆盖到的月份 "YYYY-MM", 升序去重 */
export function monthsCovering(days: string[]): string[] {
  return [...new Set(days.map((d) => d.slice(0, 7)))].sort();
}

/** 某月的所有日期 "YYYY-MM-DD"; 给了 upTo 则截止到 upTo (含), 不画未来的日子 */
export function daysOfMonth(month: string, upTo?: string): string[] {
  const [y, m] = month.split("-").map(Number);
  const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const all = Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`);
  return upTo ? all.filter((d) => d <= upTo) : all;
}

export interface DayPoint {
  date: string;
  /** 轴标签 "MM-DD" */
  label: string;
  user_queries: number;
  total_tokens: number;
  image_count: number;
}

/**
 * 把 (可能来自多个月的) 每日明细拼成指定日期序列; 没数据的日子补 0,
 * 这样折线不会把「没人用」的日子跳过去。
 */
export function buildDailySeries(days: string[], ...rowSets: Array<DailyStatRow[] | undefined>): DayPoint[] {
  const byDate = new Map<string, DailyStatRow>();
  for (const rows of rowSets) for (const r of rows ?? []) byDate.set(r.date, r);
  return days.map((date) => {
    const r = byDate.get(date);
    return {
      date,
      label: date.slice(5),
      user_queries: r?.user_queries || 0,
      total_tokens: r?.total_tokens || 0,
      image_count: r?.image_count || 0,
    };
  });
}

export const ZERO_TOTALS: UsageTotals = {
  requests: 0, user_queries: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0, image_count: 0,
};

/** 某一天的合计 (没有记录则全 0) */
export function dayTotals(rows: DailyStatRow[] | undefined, date: string): UsageTotals {
  const r = rows?.find((x) => x.date === date);
  return r ? { ...ZERO_TOTALS, ...stripNulls(r) } : { ...ZERO_TOTALS };
}

/**
 * 某月合计: 按 month 精确匹配 (旧版直接取 monthly[0], 本月还没有数据时会误拿上个月)。
 */
export function monthTotals(rows: MonthlyStatRow[] | undefined, month: string): UsageTotals {
  const r = rows?.find((x) => x.month === month);
  return r ? { ...ZERO_TOTALS, ...stripNulls(r) } : { ...ZERO_TOTALS };
}

function stripNulls<T extends object>(r: T): Partial<UsageTotals> {
  const out: Partial<UsageTotals> = {};
  for (const k of Object.keys(ZERO_TOTALS) as Array<keyof UsageTotals>) {
    const v = (r as Record<string, unknown>)[k];
    out[k] = typeof v === "number" ? v : 0;
  }
  return out;
}

export interface ShareItem {
  key: string;
  label: string;
  value: number;
  /** 占总量比例 0-1 */
  share: number;
  /** 折叠出来的「其他」 */
  other?: boolean;
}

/**
 * 份额条: 按值降序, 超过 maxItems 时把尾部折叠成「其他 (N 项)」放最后。
 * 值为 0 的项不画。
 */
export function shareItems<T>(
  rows: T[] | undefined,
  getLabel: (r: T) => string,
  getValue: (r: T) => number,
  maxItems = 8,
): ShareItem[] {
  const items = (rows ?? [])
    .map((r) => ({ label: getLabel(r), value: getValue(r) || 0 }))
    .filter((x) => x.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = items.reduce((s, x) => s + x.value, 0);
  if (total === 0) return [];
  const head = items.length > maxItems ? items.slice(0, maxItems - 1) : items;
  const tail = items.length > maxItems ? items.slice(maxItems - 1) : [];
  const out: ShareItem[] = head.map((x, i) => ({
    key: `${i}:${x.label}`, label: x.label, value: x.value, share: x.value / total,
  }));
  if (tail.length) {
    const v = tail.reduce((s, x) => s + x.value, 0);
    out.push({ key: "__other", label: `其他 (${tail.length} 项)`, value: v, share: v / total, other: true });
  }
  return out;
}

/** 紧凑数值: 1,284 / 12.9K / 4.2M (统计卡、坐标轴用) */
export function compactNum(n: number): string {
  const a = Math.abs(n);
  if (a < 10_000) return n.toLocaleString("en-US");
  if (a < 1_000_000) return `${trim1(n / 1000)}K`;
  if (a < 1_000_000_000) return `${trim1(n / 1_000_000)}M`;
  return `${trim1(n / 1_000_000_000)}B`;
}

function trim1(x: number): string {
  return x.toFixed(1).replace(/\.0$/, "");
}

/** 百分比: 0.4567 → "45.7%"; 不足 0.1% 显示 "<0.1%" */
export function pct(share: number): string {
  if (share > 0 && share < 0.001) return "<0.1%";
  return `${(share * 100).toFixed(1).replace(/\.0$/, "")}%`;
}
